import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const cardVariants = cva("rounded-lg border bg-card text-card-foreground transition-all duration-300", {
  variants: {
    variant: {
      default: "hover:shadow-[0px_0px_6px_0px_rgba(0,_0,_0,_0.1)]",
      elevated: "shadow-md hover:shadow-lg hover:-translate-y-1",
      interactive: "hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 cursor-pointer",
      outline: "border-2 shadow-none hover:shadow-sm",
      ghost: "border-none shadow-none hover:bg-accent/50"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant, ...props }, ref) => (
  <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} data-oid="crd01ax" />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} data-oid="crdh02b" />
  )
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement> & { as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" }
>(({ className, as: Component = "h3", ...props }, ref) => (
  <Component ref={ref} className={cn("text-lg leading-none font-semibold tracking-tight", className)} {...props} />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-muted-foreground text-sm", className)} {...props} />
  )
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0 pb-12", className)} {...props} data-oid="crdc03c" />
  )
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} data-oid="crdf04d" />
  )
);
CardFooter.displayName = "CardFooter";

// New component: Card with badge/status indicator
interface CardWithBadgeProps extends CardProps {
  badgeText?: string;
  badgeVariant?: "default" | "outline" | "secondary" | "destructive" | "success" | "warning" | "info";
  badgePosition?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
}

const badgeVariantClasses = {
  default: "bg-primary text-primary-foreground",
  outline: "border border-primary bg-transparent text-primary",
  secondary: "bg-secondary text-secondary-foreground",
  destructive: "bg-destructive text-destructive-foreground",
  success: "bg-green-500 text-white",
  warning: "bg-yellow-500 text-white",
  info: "bg-blue-500 text-white"
};

const badgePositionClasses = {
  "top-right": "-top-2 -right-2",
  "top-left": "-top-2 -left-2",
  "bottom-right": "-bottom-2 -right-2",
  "bottom-left": "-bottom-2 -left-2"
};

const CardWithBadge = React.forwardRef<HTMLDivElement, CardWithBadgeProps>(
  ({ className, variant, badgeText, badgeVariant = "default", badgePosition = "top-right", ...props }, ref) => {
    if (!badgeText) return <Card ref={ref} variant={variant} className={className} {...props} />;

    return (
      <div className="relative" data-oid="crdw05e">
        <Card ref={ref} variant={variant} className={className} {...props} />
        <span
          className={cn(
            "absolute rounded-full px-2 py-1 text-xs font-medium",
            badgeVariantClasses[badgeVariant],
            badgePositionClasses[badgePosition]
          )}
        >
          {badgeText}
        </span>
      </div>
    );
  }
);
CardWithBadge.displayName = "CardWithBadge";

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, CardWithBadge };
